import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/kit";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import PhotoUpload from "@/components/PhotoUpload";
import FamilyMembers from "@/components/FamilyMembers";

export default function MyAccount() {
  const [member, setMember] = useState(undefined);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: { street: "", city: "", state: "", zip: "" } });
  const [saving, setSaving] = useState(false);
  const [pinEdit, setPinEdit] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [showPin, setShowPin] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/membership/me");
    setMember(r.data.member);
    if (r.data.member) {
      const a = r.data.member.address || {};
      setForm({ name: r.data.member.name || "", email: r.data.member.email || "", phone: r.data.member.phone || "",
        address: { street: a.street || "", city: a.city || "", state: a.state || "", zip: a.zip || "" } });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name can't be empty.");
    setSaving(true);
    try {
      const r = await api.put("/membership/me", form);
      setMember(r.data.member);
      toast.success("Your details were saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const changePin = async () => {
    if (!/^\d{4}$/.test(newPin)) return toast.error("PIN must be 4 digits.");
    try { await api.put("/membership/me/pin", { pin: newPin }); toast.success("PIN updated"); setPinEdit(false); setNewPin(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update PIN"); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setAddr = (k) => (e) => setForm({ ...form, address: { ...form.address, [k]: e.target.value } });
  const inputCls = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[44px] text-[15px]";

  if (member === undefined) return <div className="text-ink/60 py-8">Loading…</div>;
  if (member === null) return <div className="text-ink/60 py-8" data-testid="account-empty">No membership record found on your account.</div>;

  return (
    <div className="space-y-5" data-testid="my-account-page">
      <div>
        <h1 className="font-zilla text-3xl font-semibold mb-1">My Account</h1>
        <p className="text-[16px] text-ink/70">Your details, photo, and check-in PIN.</p>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="account-identity">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[14px]">
          <div><span className="text-ink/50">Member #</span> <span className="font-semibold">{member.member_number}</span></div>
          <div><span className="text-ink/50">Type</span> <span className="font-semibold capitalize">{(member.account_type || "").replace(/_/g, " ")}</span></div>
          <div><span className="text-ink/50">Status</span> <span className="font-semibold capitalize">{member.status}</span></div>
        </div>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="account-photo">
        <h3 className="font-zilla text-lg font-semibold mb-3">Profile photo</h3>
        <PhotoUpload endpoint="/membership/me/photo" photoUrl={member.photo_url}
          onUploaded={(url) => setMember({ ...member, photo_url: url })} testid="account-photo-upload" />
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-5 space-y-3" data-testid="account-details">
        <h3 className="font-zilla text-lg font-semibold">Your details</h3>
        <div>
          <label className="text-[13px] font-semibold block mb-1">Full name</label>
          <input data-testid="account-name" value={form.name} onChange={set("name")} className={inputCls} />
        </div>
        <div>
          <label className="text-[13px] font-semibold block mb-1">Email</label>
          <input data-testid="account-email" type="email" value={form.email} onChange={set("email")} className={inputCls} />
        </div>
        <div>
          <label className="text-[13px] font-semibold block mb-1">Phone</label>
          <input data-testid="account-phone" value={form.phone} onChange={set("phone")} className={inputCls} />
        </div>
        <div data-testid="account-address">
          <label className="text-[13px] font-semibold block mb-1">Mailing address</label>
          <input data-testid="account-address-street" value={form.address.street} onChange={setAddr("street")} placeholder="Street address" className={inputCls + " mb-2"} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input data-testid="account-address-city" value={form.address.city} onChange={setAddr("city")} placeholder="City" className={inputCls} />
            <input data-testid="account-address-state" value={form.address.state} onChange={setAddr("state")} placeholder="State" className={inputCls} />
            <input data-testid="account-address-zip" value={form.address.zip} onChange={setAddr("zip")} placeholder="ZIP" className={inputCls} />
          </div>
        </div>
        <button data-testid="account-save" onClick={save} disabled={saving}
          className="bg-maroon text-cream font-semibold rounded-lg px-5 py-2.5 min-h-[44px] text-[15px] disabled:opacity-50 inline-flex items-center gap-2">
          {saving && <Icon name="RefreshCw" className="w-4 h-4 animate-spin" />} Save changes
        </button>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="account-pin">
        <h3 className="font-zilla text-lg font-semibold mb-2">Your check-in PIN</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-xl tracking-widest bg-warm-cream border border-ink/15 px-3 py-1 rounded" data-testid="account-pin-value">{showPin ? member.pin : "••••"}</span>
          <Button variant="secondary" data-testid="account-toggle-pin" onClick={() => setShowPin((s) => !s)} className="px-3 py-2 min-h-[40px] text-[14px]">{showPin ? "Hide" : "Show"}</Button>
          <Button variant="secondary" data-testid="account-change-pin" onClick={() => setPinEdit((v) => !v)} className="px-3 py-2 min-h-[40px] text-[14px]">Change PIN</Button>
        </div>
        {pinEdit && (
          <div className="flex gap-2 mt-3">
            <input data-testid="account-new-pin" inputMode="numeric" maxLength={4} placeholder="New 4-digit PIN" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
            <Button data-testid="account-save-pin" onClick={changePin} className="px-4 min-h-[44px]">Save</Button>
          </div>
        )}
      </div>
      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="account-family">
        <h3 className="font-zilla text-lg font-semibold mb-1">Family / household members</h3>
        <p className="text-[13px] text-ink/55 mb-3">People who live with you. Staff can promote any of them to their own membership later.</p>
        <FamilyMembers base="/membership/me" family={member.family || []} onChanged={load} testid="account-family-editor" />
      </div>
    </div>
  );
}
